/**
 * Format cents to dollar string (e.g., 1250 -> "$12.50")
 */
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

/**
 * Parse dollar string to cents (e.g., "12.50" -> 1250)
 * Returns 0 for invalid input
 */
export function parseCurrency(value: string): number {
  const num = parseFloat(value.replace(/[^0-9.-]/g, ""));
  if (isNaN(num)) return 0;
  return Math.round(num * 100);
}

/**
 * Format cents to input value (e.g., 1250 -> "12.50")
 */
export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Format decimal hours to "Xh Ym" string
 * Examples: 3.5 -> "3h 30m", 2.0 -> "2h", 0.75 -> "45m"
 */
export function formatHours(decimalHours: number): string {
  if (decimalHours === 0) return "0h";

  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/**
 * Format decimal hours to compact "Xh Ym" string, rounded to nearest 15 minutes
 * Examples: 3.5 -> "3h 30m", 2.0 -> "2h", 0.82 -> "0h 45m"
 */
export function formatHoursCompact(decimalHours: number): string {
  const h = Math.floor(decimalHours);
  // Round to nearest 15-minute increment (0, 15, 30, 45)
  const rawMinutes = (decimalHours - h) * 60;
  const m = Math.round(rawMinutes / 15) * 15;
  // Handle case where rounding pushes to 60 minutes
  if (m === 60) {
    return `${h + 1}h`;
  }
  if (h === 0 && m === 0) return "0h";
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Format minutes to "Xh Ym" string
 * Examples: 210 -> "3h 30m", 120 -> "2h", 45 -> "45m"
 */
export function formatMinutes(minutes: number): string {
  if (minutes === 0) return "0h";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Format time string based on preference
 * Input: HH:MM (24-hour format)
 * Output: 12h format "3:30 PM" or 24h format "15:30"
 */
export function formatTimeWithPreference(
  time: string,
  format: "12h" | "24h"
): string {
  if (format === "24h") return time;

  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Format fuel price with 3 decimal places (US gas station convention ends in 9)
 * Input: cents (e.g., 189 for $1.89, 1899 for $1.899)
 * Output: string with 3 decimal places (e.g., "$1.899")
 */
export function formatFuelPrice(cents: number): string {
  return `$${(cents / 100).toFixed(3)}`;
}

/**
 * Convert fuel price cents to input value with 3 decimal places
 * Input: cents (e.g., 1899)
 * Output: string (e.g., "1.899")
 */
export function fuelPriceCentsToInput(cents: number): string {
  return (cents / 100).toFixed(3);
}
